---
title: React Context Api
date: 2018-09-09T14:56:45-05:00
draft: false
image: "images/react_context_diagram.png"
---

The React context api is a simple messaging system similar in concept to the Publish/Subscribe pattern. It has three main parts you need to implement to use it: Context, Provider, and Consumer

* Context: The store of value. Also where the Provider and Consumer come from
* Provider: The publisher/emitter of the value aka the source
* Consumer: The subscriber/listener for the value to be received to aka the destination 

![React Context API Diagram](/static/images/react_context_diagram.png)

### What about Redux?
* Context api doesn't handle changes to state safely, it just does simple publish/subscribe. It won't handle conflicts in state, or keep multiple asynchronous events from mutating each other. 
* If you just need to pass some data down to a deeply nested component, wrapped components are all you need
* If you just need to have a change propagate to multiple locations, the context api is all you need
* If you need to handle how state changes over the usage of your application, Redux is potentially a better solution

---

## Examples

### Context
* Creates a `Provider` and `Consumer` pair
* You can also pass a default value to the context like `React.createContext(myDefaultValue)`, but it will only be used when a `Consumer` doesn't have a matching `Provider` above it in the tree

>src/contexts/index.js

```javascript
import React from "react";

export default ListContext = React.createContext()
```


### Provider
* This is the emitter/publisher of the React context api.
* Any change of the `Provider`'s `value` property will signal a change to all `Consumer`s of the context, and trigger a render of them.


>src/containers/app.js

```javascript
import React, { Component } from "react";
import { Provider } from "../contexts/";
import Input from "../components/input"; 
import List from "../components/list";


export default class extends Component{
    constructor (props) {
        super(props);
        
        // I used 'this.state' to be the object I passed to my provider,
        // but it can really be anything, including a single value 
        this.state = {
            itemList: [
                "Item 1: Context API, A New Hope",
                "Item 2: Redux Strikes Back",
                "Item 3: Return of Idempodency"
            ],
        }
    }
    
    // ...
    
    render () {
        return (
            <Provider value={this.state}>
                <div>
                    <h2>Todo List</h2>
                    <Input />
                    <List />
                </div>
            </Provider> 
        );
    }
}
```

### Consumer
* The `Consumer` does not need to be attached to the `Provider`, or passed down by the `Provider`. You can just import the context in any random descendents, and be able to listen to changes from the `Provider`
* Just like `Provider`, `Consumer` _has_ to live in the JSX of the `render` method (or a sub render method).
* The first and only direct child of a `Consumer` is an anonymous function that is passed a single value.
* This single value is the value set in the `Provider`'s `value` property.

>src/components/list.js

```javascript
import React, { Component } from "react";
import { Consumer } from "../contexts/";

export default class extends Component {

    // ...

    render () {
        return (
            <Consumer>
                {(context) => (
                    <ul>
                        {context.items.map((title, index) => (
                            <li key={index}>{title}</li>
                        ))}
                    </ul>
                )}
            </Consumer>
        )
    }
}
```

---

## How do I update the Context's value from a Consumer?
The context api maintains React's strict unidirectional data flow. If you need a `Consumer` to have the ability to update the `Context`'s value, you need to have a closure passed down from the `Provider` as part of it's value (usually a property of the object that the `Provider`'s value is set to).
Then you can access the closure from the value passed down from the `Provider`, and trigger whatever change you need as it still has the lexical scope of where it was created.

### Example

#### Provider

>src/containers/app.js

```javascript
import React, { Component } from "react";
import { Provider } from "../contexts/";
import Input from "../components/input"; 
import List from "../components/list";


export default class extends Component{
    constructor (props) {
        super(props);
        this.state = {
            // The arrow function auto-binds 'this'. If you 
            // use a normal function/method, make sure to 
            // bind the appropriate context
            updateSelection: (name) => {
                this.setState({selectedName: name});
            },
            selectedName: ""
        }
    }
    
    // ...
    
    render () {
        return (
            <Provider value={this.state}>
                <div>
                    <h2>Todo List</h2>
                    <Input />
                    <List />
                </div>
            </Provider> 
        );
    }
}
```

#### Consumer

>src/components/list.js

```javascript
import React, { Component } from "react";
import { Consumer } from "../contexts/";

export default class extends Component {

    // ...

    render () {
        return (
            <Consumer>
                {(context) => (
                    <div>
                        { //  You can call it directly, or pass }
                        { //  it to a method you create to be   }
                        { //  used there                        }
                        <div onClick={context.updateSelection("Joe")}>
                            Select Joe!
                        </div>
                    </div>
                )}
            </Consumer>
        )
    }
}
```
